/**
 * Design tokens — single source of truth para valores de diseño.
 *
 * Consumir desde JS via import, o desde CSS via custom properties (ver global.css).
 * Ver ADR-018: docs/architecture/decisions/018-sistema-diseno-ui-tokens-primitives.md
 */

export const colors = {
  // Brand
  primary: '#00468B',
  primaryDark: '#003366',
  primaryLight: '#2563EB',
  primaryBg: 'rgba(0, 70, 139, 0.08)',

  // Semánticos
  success: '#059669',
  successDark: '#047857',
  successBg: '#D1FAE5',

  error: '#EF4444',
  errorDark: '#DC2626',
  errorBg: '#FEE2E2',

  warning: '#F59E0B',
  warningDark: '#D97706',
  warningBg: '#FEF3C7',

  info: '#3B82F6',
  infoDark: '#2563EB',
  infoBg: '#DBEAFE',

  // Neutros (escala Tailwind-ish consistente)
  gray50: '#F9FAFB',
  gray100: '#F3F4F6',
  gray200: '#E5E7EB',
  gray300: '#D1D5DB',
  gray400: '#9CA3AF',
  gray500: '#6B7280',
  gray600: '#4B5563',
  gray700: '#374151',
  gray800: '#1F2937',
  gray900: '#111827',

  // Superficies semánticas
  bg: '#F9FAFB',
  surface: '#FFFFFF',
  border: '#E5E7EB',
  borderStrong: '#D1D5DB',

  // Texto semántico
  text: '#111827',
  textMuted: '#6B7280',
  textSubtle: '#9CA3AF',
  textInverse: '#FFFFFF',
};

/**
 * Spacing scale (4px base). Indexado para permitir spacing[1], spacing[2], etc.
 * Mapping: 0=0, 1=4, 2=8, 3=12, 4=16, 5=24, 6=32, 7=48, 8=64.
 */
export const spacing = [0, 4, 8, 12, 16, 24, 32, 48, 64];

export const typography = {
  fontFamily: {
    sans: "'Inter', sans-serif",
    mono: "'SF Mono', 'Fira Code', Menlo, Consolas, monospace",
  },
  fontSize: {
    xs: '11px',
    sm: '12px',
    base: '14px',
    md: '16px',
    lg: '18px',
    xl: '20px',
    '2xl': '24px',
    '3xl': '28px',
    '4xl': '48px',
  },
  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.75,
  },
};

export const radius = {
  none: 0,
  sm: 4,
  md: 8,
  lg: 12,
  full: 9999,
};

export const shadow = {
  sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.05)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.05)',
};

export const zIndex = {
  base: 0,
  dropdown: 1000,
  sticky: 1020,
  modal: 1040,
  toast: 1060,
  // Por encima de TODO. Hay overlays con z-index literal de 2000, 2500 y 9999 repartidos por la
  // app (folders, ACL, claves de API, notificaciones) que no usan estos tokens; un aviso que
  // bloquea la sesion tiene que ganarles a todos, o queda de fondo con un formulario usable
  // adelante (#171).
  sessionBlock: 10050,
};

export const transitions = {
  fast: '0.15s ease',
  base: '0.2s ease',
  slow: '0.3s ease',
};

const tokens = { colors, spacing, typography, radius, shadow, zIndex, transitions };
export default tokens;
